/**
 * src/pages/FounderFeedPage.jsx
 * ──────────────────────────────
 * Founder activity feed — shows what every AI agent and the founder
 * have done inside a workspace, grouped by date, filterable by type.
 *
 * Route:  /dashboard/feed?workspace_id=…
 * API:    GET /api/workspaces/{workspace_id}/feed
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AppShell, TopNav, NavDrawer, BodyGrid, PageBody, useDrawer } from '../components/Layout';
import { apiFetch } from '../services/api';
import { useWorkspaces } from '../hooks/useWorkspaces';

// ═══════════════════════════════════════════════════════════════════════════════
// resolveActionUrl — single centralised URL factory
// Pass the entire feed record; returns a string URL or null.
// ═══════════════════════════════════════════════════════════════════════════════

export function resolveActionUrl(item) {
  if (!item) return null;

  const {
    target_type,
    target_id,
    workspace_id,
    artifact_id,
    task_id,
    live_url,
    action_url,   // stored value — used only as last-resort fallback
    event_type,
  } = item;

  // ── task ──────────────────────────────────────────────────────────────────
  if (target_type === 'task') {
    const id = target_id || task_id;
    if (id) return `/dashboard/tasks/${id}?workspace_id=${workspace_id}`;
  }

  // ── artifact / site_live ──────────────────────────────────────────────────
  if (target_type === 'artifact' || event_type === 'site_live') {
    if (event_type === 'site_live' && live_url) return live_url;           // external
    const id = target_id || artifact_id;
    if (id) return `/dashboard/artifacts/${id}`;
  }

  // ── message ───────────────────────────────────────────────────────────────
  if (target_type === 'message') {
    return `/dashboard/ceo_messages?workspace_id=${workspace_id}`;
  }

  // ── workspace ─────────────────────────────────────────────────────────────
  if (target_type === 'workspace') {
    return `/dashboard?workspace_id=${workspace_id}`;
  }

  // ── last-resort: stored action_url from DB ────────────────────────────────
  return action_url || null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════════

function timeAgo(ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 172800) return 'Yesterday';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function dateLabel(ts) {
  if (!ts) return 'Earlier';
  const d     = new Date(ts);
  const today = new Date();
  const diff  = Math.floor((today - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function groupByDate(items) {
  const groups = [];
  const seen   = new Map();
  for (const item of items) {
    const label = dateLabel(item.created_at);
    if (!seen.has(label)) {
      seen.set(label, groups.length);
      groups.push({ label, items: [] });
    }
    groups[seen.get(label)].items.push(item);
  }
  return groups;
}

// ── Actor meta ────────────────────────────────────────────────────────────────

const ACTOR_META = {
  ceo:       { icon: '🧠', color: '#5b6af0', bg: 'rgba(91,106,240,0.10)' },
  content:   { icon: '✍️',  color: '#e0882a', bg: 'rgba(224,136,42,0.10)' },
  marketing: { icon: '📣', color: '#d94fa0', bg: 'rgba(217,79,160,0.10)' },
  builder:   { icon: '🔧', color: '#2a9d8f', bg: 'rgba(42,157,143,0.10)' },
  ops:       { icon: '⚙️',  color: '#6c757d', bg: 'rgba(108,117,125,0.10)' },
  research:  { icon: '🔍', color: '#7b5ea7', bg: 'rgba(123,94,167,0.10)' },
  founder:   { icon: '👤', color: '#1a1a1a', bg: 'rgba(0,0,0,0.07)' },
  system:    { icon: '🤖', color: '#8d99ae', bg: 'rgba(141,153,174,0.10)' },
};

function actorMeta(type) {
  return ACTOR_META[type?.toLowerCase()] || ACTOR_META.system;
}

// ── Badge meta ────────────────────────────────────────────────────────────────

function badgeForEvent(event_type) {
  switch (event_type) {
    case 'task_done':      return { label: 'Done',           color: '#1a7a5a', bg: 'rgba(93,202,165,0.12)' };
    case 'task_blocked':   return { label: 'Blocked',        color: '#c8563a', bg: 'rgba(200,86,58,0.10)'  };
    case 'task_started':   return { label: 'In Progress',    color: '#378add', bg: 'rgba(55,138,221,0.10)' };
    case 'task_created':   return { label: 'New Task',       color: '#6c757d', bg: 'rgba(108,117,125,0.08)' };
    case 'site_live':      return { label: '🌐 Live',        color: '#1a7a5a', bg: 'rgba(93,202,165,0.12)' };
    case 'artifact_created': return { label: 'Artifact',     color: '#7b5ea7', bg: 'rgba(123,94,167,0.10)' };
    default:               return null;
  }
}

const isExternal = (url) => url && (url.startsWith('http://') || url.startsWith('https://'));

// ═══════════════════════════════════════════════════════════════════════════════
// FeedSummary
// ═══════════════════════════════════════════════════════════════════════════════

function FeedSummary({ summary }) {
  if (!summary) return null;
  const tiles = [
    { label: 'Created today',   value: summary.tasks_created,   icon: '📋', color: '#378add' },
    { label: 'Completed today', value: summary.tasks_completed, icon: '✅', color: '#1a7a5a' },
    { label: 'Blocked',         value: summary.blocked,         icon: '🚧', color: '#c8563a' },
    { label: 'Sites live',      value: summary.sites_live,      icon: '🌐', color: '#7b5ea7' },
  ];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '10px', padding: '16px 24px', borderBottom: '1px solid var(--paper-3)',
      background: 'var(--white)', flexShrink: 0,
    }}>
      {tiles.map(t => (
        <div key={t.label} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
          padding: '12px 14px', borderRadius: 'var(--r)',
          background: 'var(--paper)', border: '1px solid var(--paper-3)',
          gap: '4px',
        }}>
          <span style={{ fontSize: '18px', lineHeight: 1 }}>{t.icon}</span>
          <span style={{
            fontSize: '22px', fontWeight: '700', color: t.value > 0 ? t.color : 'var(--ink-mute)',
            fontFamily: 'var(--serif)', lineHeight: 1.1, marginTop: '4px',
          }}>
            {t.value ?? 0}
          </span>
          <span style={{ fontSize: '10px', color: 'var(--ink-mute)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {t.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FeedFilters
// ═══════════════════════════════════════════════════════════════════════════════

const FILTER_TABS = [
  { key: 'all',     label: 'All' },
  { key: 'tasks',   label: 'Tasks' },
  { key: 'sites',   label: 'Sites' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'founder', label: 'Founder' },
];

function FeedFilters({ active, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: '4px', padding: '12px 24px',
      borderBottom: '1px solid var(--paper-3)',
      background: 'var(--white)', flexShrink: 0, flexWrap: 'wrap',
    }}>
      {FILTER_TABS.map(tab => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          style={{
            padding: '5px 14px', borderRadius: '100px',
            border: active === tab.key ? '1px solid var(--ink)' : '1px solid var(--paper-3)',
            background: active === tab.key ? 'var(--ink)' : 'var(--white)',
            color: active === tab.key ? 'var(--paper)' : 'var(--ink-soft)',
            fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: active === tab.key ? '600' : '400',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FeedItemRow
// ═══════════════════════════════════════════════════════════════════════════════

function FeedItemRow({ item }) {
  const [expanded, setExpanded] = useState(false);
  const meta    = actorMeta(item.actor_type);
  const badge   = badgeForEvent(item.event_type);
  const url     = resolveActionUrl(item);
  const ext     = isExternal(url);

  function handleClick(e) {
    // If there's a navigable URL, let the Link handle it; otherwise expand.
    if (!url) {
      e.preventDefault();
      if (item.body) setExpanded(v => !v);
    }
  }

  const inner = (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '12px',
        padding: '12px 16px',
        transition: 'background 0.12s',
        cursor: url ? 'pointer' : item.body ? 'pointer' : 'default',
        borderRadius: expanded ? '0' : undefined,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--paper)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Unread dot */}
      <div style={{ paddingTop: '5px', width: '6px', flexShrink: 0 }}>
        {!item.is_read && (
          <div style={{
            width: '6px', height: '6px', borderRadius: '50%',
            background: meta.color,
          }} />
        )}
      </div>

      {/* Actor avatar */}
      <div style={{
        width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
        background: meta.bg, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '15px', marginTop: '1px',
      }}>
        {meta.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Actor name */}
          {item.actor_name && (
            <span style={{
              fontSize: '11px', fontWeight: '600', color: meta.color,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {item.actor_name}
            </span>
          )}

          {/* Badge */}
          {badge && (
            <span style={{
              fontSize: '10px', fontWeight: '600', padding: '2px 8px',
              borderRadius: '100px', background: badge.bg, color: badge.color,
              letterSpacing: '0.03em',
            }}>
              {badge.label}
            </span>
          )}

          {/* External link indicator */}
          {ext && (
            <span style={{ fontSize: '10px', color: 'var(--ink-mute)' }}>↗</span>
          )}
        </div>

        {/* Title */}
        <p style={{
          margin: '2px 0 0', fontSize: '13.5px', fontWeight: '500',
          color: 'var(--ink)', lineHeight: 1.45,
          overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: expanded ? 'normal' : 'nowrap',
        }}>
          {item.title}
        </p>

        {/* Body — one truncated line, or full if expanded */}
        {item.body && (
          <p style={{
            margin: '3px 0 0', fontSize: '12px', color: 'var(--ink-mute)',
            lineHeight: 1.55,
            overflow: expanded ? 'visible' : 'hidden',
            textOverflow: expanded ? 'clip' : 'ellipsis',
            whiteSpace: expanded ? 'pre-wrap' : 'nowrap',
            maxHeight: expanded ? 'none' : '1.55em',
            animation: expanded ? 'feedExpand 0.2s ease' : undefined,
          }}>
            {item.body}
          </p>
        )}
      </div>

      {/* Timestamp */}
      <span style={{
        fontSize: '11px', color: 'var(--ink-mute)', flexShrink: 0,
        marginTop: '4px', whiteSpace: 'nowrap',
      }}>
        {timeAgo(item.created_at)}
      </span>
    </div>
  );

  // Wrap in Link if navigable, otherwise plain div
  return (
    <div
      style={{
        borderBottom: '1px solid var(--paper-2)',
        background: 'var(--white)',
      }}
      onClick={url ? undefined : handleClick}
    >
      {url ? (
        ext
          ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}>{inner}</a>
          : <Link to={url} style={{ textDecoration: 'none', display: 'block' }}>{inner}</Link>
      ) : inner}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FeedDateGroup
// ═══════════════════════════════════════════════════════════════════════════════

function FeedDateGroup({ label, items }) {
  return (
    <div style={{ marginBottom: '8px' }}>
      {/* Date label */}
      <div style={{
        padding: '10px 24px 6px',
        fontSize: '11px', fontWeight: '700', color: 'var(--ink-mute)',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <span>{label}</span>
        <div style={{ flex: 1, height: '1px', background: 'var(--paper-3)' }} />
        <span style={{ fontWeight: '400', letterSpacing: 0, textTransform: 'none' }}>
          {items.length} event{items.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Cards */}
      <div style={{
        margin: '0 16px',
        border: '1px solid var(--paper-3)',
        borderRadius: 'var(--r)',
        overflow: 'hidden',
        background: 'var(--white)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        {items.map((item, i) => (
          <FeedItemRow key={item.id} item={item} isLast={i === items.length - 1} />
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Empty & loading states
// ═══════════════════════════════════════════════════════════════════════════════

function EmptyFeed({ filter }) {
  return (
    <div style={{ padding: '64px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: '40px', marginBottom: '16px' }}>📭</div>
      <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--ink)', margin: '0 0 6px' }}>
        No activity yet
      </p>
      <p style={{ fontSize: '13px', color: 'var(--ink-mute)', margin: 0, lineHeight: 1.6 }}>
        {filter === 'all'
          ? 'Your AI team activity will appear here.'
          : `No ${filter} events found. Try switching to "All".`}
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{
          height: '64px', borderRadius: 'var(--r)',
          background: 'var(--paper-2)',
          animation: 'shimmer 1.4s ease infinite',
          animationDelay: `${i * 80}ms`,
        }} />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Page root
// ═══════════════════════════════════════════════════════════════════════════════

const PAGE_LIMIT = 20;

export default function FounderFeedPage() {
  const [searchParams]      = useSearchParams();
  const { activeWorkspace } = useWorkspaces();
  const drawer              = useDrawer();
  const navigate            = useNavigate();

  const [filter,   setFilter]   = useState('all');
  const [items,    setItems]    = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [total,    setTotal]    = useState(0);
  const [hasMore,  setHasMore]  = useState(false);
  const [offset,   setOffset]   = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,    setError]    = useState(null);

  // Keep URL in sync with active workspace
  useEffect(() => {
    if (!activeWorkspace?.id) return;
    const cur = searchParams.get('workspace_id');
    if (cur !== activeWorkspace.id) {
      navigate(`/dashboard/feed?workspace_id=${activeWorkspace.id}`, { replace: true });
    }
  }, [activeWorkspace?.id]);

  const workspaceId = searchParams.get('workspace_id') || activeWorkspace?.id || '';

  // ── Fetch (initial / filter change) ───────────────────────────────────────
  const fetchFeed = useCallback(async (opts = {}) => {
    if (!workspaceId) return;
    const isLoadMore = opts.loadMore === true;
    const nextOffset = isLoadMore ? opts.offset : 0;

    isLoadMore ? setLoadingMore(true) : setLoading(true);
    setError(null);

    try {
      const qs   = `?limit=${PAGE_LIMIT}&offset=${nextOffset}&filter=${filter}`;
      const data = await apiFetch(`/api/workspaces/${workspaceId}/feed${qs}`);

      if (isLoadMore) {
        setItems(prev => [...prev, ...(data.items || [])]);
      } else {
        setItems(data.items || []);
        setSummary(data.summary || null);
      }
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      setOffset(nextOffset + PAGE_LIMIT);
    } catch (e) {
      setError('Failed to load feed. Please try again.');
    } finally {
      isLoadMore ? setLoadingMore(false) : setLoading(false);
    }
  }, [workspaceId, filter]);

  useEffect(() => {
    setOffset(0);
    setItems([]);
    fetchFeed();
  }, [workspaceId, filter]);

  function handleFilterChange(f) {
    setFilter(f);
  }

  function handleLoadMore() {
    fetchFeed({ loadMore: true, offset });
  }

  const groups = groupByDate(items);

  return (
    <AppShell>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer { 0%,100% { opacity:0.5; } 50% { opacity:1; } }
        @keyframes feedExpand {
          from { opacity: 0; max-height: 0; }
          to   { opacity: 1; max-height: 200px; }
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--paper-3); border-radius: 4px; }
      `}</style>

      <TopNav
        breadcrumb="Founder Feed"
        drawerOpen={drawer.open}
        onToggleDrawer={drawer.toggle}
        drawerPinned={drawer.pinned}
      />
      <NavDrawer
        open={drawer.open} onClose={drawer.close}
        pinned={drawer.pinned} onTogglePin={drawer.togglePin}
      />

      <BodyGrid drawerPinned={drawer.pinned}>
        <PageBody style={{ padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 52px)', overflow: 'hidden' }}>

          {/* ── Page header ── */}
          <div style={{
            padding: '20px 24px 16px', borderBottom: '1px solid var(--paper-3)',
            background: 'var(--white)', flexShrink: 0,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          }}>
            <div>
              <h1 style={{ fontFamily: 'var(--serif)', fontSize: '22px', fontWeight: '400', margin: 0 }}>
                Founder Feed
              </h1>
              <p style={{ fontSize: '12px', color: 'var(--ink-mute)', margin: '3px 0 0' }}>
                Everything your AI team has done — at a glance
              </p>
            </div>
            {total > 0 && (
              <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
                {total.toLocaleString()} events
              </span>
            )}
          </div>

          {/* ── Summary strip ── */}
          <FeedSummary summary={summary} />

          {/* ── Filter tabs ── */}
          <FeedFilters active={filter} onChange={handleFilterChange} />

          {/* ── Feed body ── */}
          <div style={{ flex: 1, overflowY: 'auto', background: 'var(--paper)' }}>

            {error && (
              <div style={{
                margin: '16px 24px', padding: '12px 16px',
                background: 'rgba(200,86,58,0.08)', border: '1px solid rgba(200,86,58,0.2)',
                borderRadius: 'var(--r)', fontSize: '13px', color: '#a03020',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                {error}
                <button onClick={() => { setError(null); fetchFeed(); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontFamily: 'var(--sans)', fontSize: '12px' }}>
                  Retry ↺
                </button>
              </div>
            )}

            {loading
              ? <LoadingSkeleton />
              : items.length === 0
                ? <EmptyFeed filter={filter} />
                : (
                  <div style={{ paddingBottom: '24px', animation: 'fadeSlideIn 0.25s ease' }}>
                    {groups.map(g => (
                      <FeedDateGroup key={g.label} label={g.label} items={g.items} />
                    ))}

                    {/* Load more */}
                    {hasMore && (
                      <div style={{ padding: '8px 24px 16px', textAlign: 'center' }}>
                        <button
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                          style={{
                            padding: '9px 28px',
                            border: '1px solid var(--paper-3)',
                            borderRadius: 'var(--r)',
                            background: 'var(--white)',
                            fontFamily: 'var(--sans)', fontSize: '13px',
                            color: 'var(--ink-soft)', cursor: loadingMore ? 'default' : 'pointer',
                            opacity: loadingMore ? 0.5 : 1, transition: 'opacity 0.15s',
                          }}
                        >
                          {loadingMore ? 'Loading…' : `Load more · ${total - items.length} remaining`}
                        </button>
                      </div>
                    )}
                  </div>
                )
            }
          </div>

        </PageBody>
      </BodyGrid>
    </AppShell>
  );
}
